import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import Tabs from '@atlaskit/tabs';
import Modal, { ModalBody } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import { NUMBER_OF_REACTIONS_TO_DISPLAY } from '../../shared/constants';
import { ReactionsList } from './ReactionsList';
import { ReactionsDialogHeader } from './ReactionsDialogHeader';
import { containerStyle } from './styles';

/**
 * Test id for the Reactions modal dialog
 */
export var RENDER_MODAL_TESTID = 'render-reactions-modal';
var getDimensions = function getDimensions(container) {
  return {
    clientWidth: container === null || container === void 0 ? void 0 : container.clientWidth,
    scrollWidth: container === null || container === void 0 ? void 0 : container.scrollWidth,
    scrollLeft: container === null || container === void 0 ? void 0 : container.scrollLeft
  };
};
var modalBodyStyle = xcss({
  marginBottom: 'space.300'
});
export var ReactionsDialog = function ReactionsDialog(_ref) {
  var _currentReactions$;
  var _ref$reactions = _ref.reactions,
    reactions = _ref$reactions === void 0 ? [] : _ref$reactions,
    _ref$handleCloseReact = _ref.handleCloseReactionsDialog,
    handleCloseReactionsDialog = _ref$handleCloseReact === void 0 ? function () {} : _ref$handleCloseReact,
    emojiProvider = _ref.emojiProvider,
    selectedEmojiId = _ref.selectedEmojiId,
    _ref$handleSelectReac = _ref.handleSelectReaction,
    handleSelectReaction = _ref$handleSelectReac === void 0 ? function () {} : _ref$handleSelectReac,
    _ref$handlePagination = _ref.handlePaginationChange,
    handlePaginationChange = _ref$handlePagination === void 0 ? function () {} : _ref$handlePagination,
    ProfileCardWrapper = _ref.ProfileCardWrapper,
    handleReactionMouseEnter = _ref.handleReactionMouseEnter;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    elementToScroll = _useState2[0],
    setElementToScroll = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    reactionsContainerRef = _useState4[0],
    setReactionsContainerRef = _useState4[1];

  // prevents accidental triggering of handlePaginationChange on initial load
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    hasNavigatedPages = _useState6[0],
    setHasNavigatedPages = _useState6[1];
  var _useState7 = useState(1),
    _useState8 = _slicedToArray(_useState7, 2),
    currentPage = _useState8[0],
    setCurrentPage = _useState8[1];
  var reactionElementsRef = useRef();
  var observerRef = useRef();
  var isSelectedEmojiViewed = useRef(false);
  var totalReactionsCount = useMemo(function () {
    return reactions.reduce(function (accum, current) {
      return accum += current === null || current === void 0 ? void 0 : current.count;
    }, 0);
  }, [reactions]);
  var maxPages = Math.max(1, Math.ceil(reactions.length / NUMBER_OF_REACTIONS_TO_DISPLAY));
  var currentReactions = useMemo(function () {
    var start = (currentPage - 1) * NUMBER_OF_REACTIONS_TO_DISPLAY;
    var end = start + NUMBER_OF_REACTIONS_TO_DISPLAY;
    return reactions.slice(start, end);
  }, [reactions, currentPage]);
  var handleNextPage = function handleNextPage() {
    setCurrentPage(function (prevPage) {
      return Math.min(prevPage + 1, maxPages);
    });
    setHasNavigatedPages(true);
  };
  var handlePreviousPage = function handlePreviousPage() {
    setCurrentPage(function (prevPage) {
      return Math.max(prevPage - 1, 1);
    });
    setHasNavigatedPages(true);
  };
  var firstEmojiOnPage = (_currentReactions$ = currentReactions[0]) === null || _currentReactions$ === void 0 ? void 0 : _currentReactions$.emojiId;
  useEffect(function () {
    // trigger the handler with the first emoji when the page changes
    if (hasNavigatedPages && firstEmojiOnPage) {
      handlePaginationChange(firstEmojiOnPage, currentPage, maxPages);
    }
  }, [hasNavigatedPages, currentPage, maxPages, firstEmojiOnPage, handlePaginationChange]);

  /* set Reactions Border Width , 8 Number of reactions to display*/
  var reactionsBorderWidth = useMemo(function () {
    return Math.ceil(reactions.length / NUMBER_OF_REACTIONS_TO_DISPLAY) * 100;
  }, [reactions]);

  /* Callback from IntersectionObserver to set/unset classNames based on visibility to toggle styles*/
  var handleNavigation = useCallback(function (entries) {
    entries.forEach(function (entry) {
      var _dataset;
      var element = entry.target;
      var emojiElement = element === null || element === void 0 ? void 0 : element.querySelector('[data-emoji-id]');
      var emojiId = emojiElement === null || emojiElement === void 0 || (_dataset = emojiElement.dataset) === null || _dataset === void 0 ? void 0 : _dataset.emojiId;
      if (entry.intersectionRatio < 1) {
        element.classList.add('disabled');
        /*Check if selectedEmoji (passed as props based on what user selects) is out of viewport */
        if (emojiId === selectedEmojiId && !isSelectedEmojiViewed.current) {
          setElementToScroll(emojiElement !== null && emojiElement !== void 0 ? emojiElement : undefined);
        }
      } else {
        if (emojiId === selectedEmojiId && !isSelectedEmojiViewed.current) {
          isSelectedEmojiViewed.current = true;
        }
        element.classList.remove('disabled');
      }
    });
  }, [selectedEmojiId]);
  useEffect(function () {
    if (elementToScroll && !isSelectedEmojiViewed.current && reactionsContainerRef) {
      isSelectedEmojiViewed.current = true;
      var parentElement = elementToScroll.closest('.reaction-elements');
      var reactionsList = document.querySelector('#reactions-dialog-tabs-list');
      var _getDimensions = getDimensions(reactionsList),
        clientWidth = _getDimensions.clientWidth;
      var offsetLeft = parentElement === null || parentElement === void 0 ? void 0 : parentElement.offsetLeft;
      /* which means emoji is not in viewport so scroll to it*/
      if (reactionsList && offsetLeft > clientWidth) {
        var scrollBy = Math.trunc(offsetLeft / clientWidth) * clientWidth;
        reactionsList.scrollLeft += scrollBy;
      }
    }
  }, [elementToScroll, reactionsContainerRef]);

  /* Set up InterSectionObserver to observer reaction elements on navigating*/
  useEffect(function () {
    if (reactionsContainerRef) {
      var options = {
        root: reactionsContainerRef,
        rootMargin: '0px',
        threshold: 1.0
      };
      observerRef.current = new IntersectionObserver(handleNavigation, options);
      reactionElementsRef.current = reactionsContainerRef.querySelectorAll('.reaction-elements');
      reactionElementsRef.current && reactionElementsRef.current.length > 0 && reactionElementsRef.current.forEach(function (child) {
        var _observerRef$current;
        observerRef === null || observerRef === void 0 || (_observerRef$current = observerRef.current) === null || _observerRef$current === void 0 || _observerRef$current.observe(child);
      });
    }
    return function () {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = undefined;
      }
    };
  }, [reactionsContainerRef, reactions, handleNavigation, selectedEmojiId]);
  var setRef = useCallback(function (node) {
    if (!reactionsContainerRef) {
      setReactionsContainerRef(node);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var selectedIndex = currentReactions.findIndex(function (reaction) {
    return reaction.emojiId === selectedEmojiId;
  });
  var onTabChange = useCallback(function (index) {
    if (index === selectedIndex) {
      return;
    }
    var emojiId = currentReactions[index].emojiId;
    handleSelectReaction(emojiId);
  }, [selectedIndex, currentReactions, handleSelectReaction]);
  return jsx(Modal, {
    onClose: handleCloseReactionsDialog,
    height: 600,
    testId: RENDER_MODAL_TESTID
    // eslint-disable-next-line jsx-a11y/no-autofocus
    ,
    autoFocus: false
  }, jsx(Tabs, {
    id: "reactions-dialog-tabs",
    onChange: onTabChange,
    selected: selectedIndex
  }, jsx(ReactionsDialogHeader, {
    totalReactionsCount: totalReactionsCount,
    maxPages: maxPages,
    handlePreviousPage: handlePreviousPage,
    handleNextPage: handleNextPage,
    currentPage: currentPage,
    emojiProvider: emojiProvider,
    currentReactions: currentReactions,
    handleCloseReactionsDialog: handleCloseReactionsDialog
  }), jsx(ModalBody, null, jsx(Box, {
    xcss: modalBodyStyle
  }, jsx("div", {
    css: containerStyle(reactionsBorderWidth),
    ref: setRef
  }, jsx(ReactionsList, {
    selectedEmojiId: selectedEmojiId,
    reactions: currentReactions,
    emojiProvider: emojiProvider,
    ProfileCardWrapper: ProfileCardWrapper
  }))))));
};